import type { TimelinePoint } from '../types';

export interface RaceBar {
  id: string;
  votes: number;
}

/** Points ready for playback: time as epoch ms, sorted */
export interface PlaybackPoint {
  t: number;
  c: string | null;
  n: number;
}

export function toPlaybackPoints(points: TimelinePoint[]): PlaybackPoint[] {
  return points.map((p) => ({ t: new Date(p.t).getTime(), c: p.c, n: p.n })).sort((a, b) => a.t - b.t);
}

/**
 * Votes per candidate up to `playhead` (epoch ms, inclusive). Blank votes (c = null) are not a bar.
 * Candidates without votes yet appear with 0 so the bars do not jump in.
 */
export function votesAt(points: PlaybackPoint[], candidateIds: string[], playhead: number): RaceBar[] {
  const counts = new Map<string, number>(candidateIds.map((id) => [id, 0]));
  for (const p of points) {
    if (p.t > playhead) break;
    if (p.c && counts.has(p.c)) counts.set(p.c, (counts.get(p.c) ?? 0) + p.n);
  }
  return candidateIds.map((id) => ({ id, votes: counts.get(id) ?? 0 }));
}

/** Bar width in %: against the percentage base of the round (so the threshold line means something) */
export function barPercent(votes: number, baseCount: number, fallbackMax: number): number {
  const denom = baseCount > 0 ? baseCount : Math.max(1, fallbackMax);
  return Math.min(100, (votes / denom) * 100);
}

/** Order bars from most to fewest votes; ties keep the candidate order of the round */
export function rankBars(bars: RaceBar[]): RaceBar[] {
  return bars.map((b, i) => ({ b, i })).sort((x, y) => y.b.votes - x.b.votes || x.i - y.i).map((x) => x.b);
}

const TICK_STEPS_SECONDS = [10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200];

export function formatElapsed(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(sec).padStart(2, '0')}`;
}

/** Marks for the timeline under the race: about 8 at most, at round durations (0:00, 5:00, 10:00 …) */
export function timelineTicks(durationMs: number): { pct: number; label: string }[] {
  const seconds = durationMs / 1000;
  if (!(seconds > 0)) return [{ pct: 0, label: '0:00' }];
  const step = TICK_STEPS_SECONDS.find((s) => seconds / s <= 8) ?? 14400;
  const ticks: { pct: number; label: string }[] = [];
  for (let t = 0; t <= seconds; t += step) ticks.push({ pct: (t / seconds) * 100, label: formatElapsed(t) });
  return ticks;
}

/** Speeds offered by the player; keys 1–4 on the projection screen map to them in order */
export const RACE_SPEEDS = [0.5, 1, 2, 4] as const;

/** Stable color slot of a candidate: its place in the election's candidate list, not in the round */
export function colorIndexOf(candidateIds: string[], id: string): number {
  const i = candidateIds.indexOf(id);
  return i < 0 ? 0 : i;
}
